import React, { useEffect, useRef, useState } from 'react'
import { Lightbulb, Sparkles, Eye, Check } from 'lucide-react'
import KaTeXRenderer from '../../../common/KaTeXRenderer'
import { aiService } from '../../../../services/ai/AIService'
import { soundService } from '../../../../services/audio/SoundEffectService'
import { LessonStatsService } from '../../../../services/stats/LessonStatsService'
import { useAuth } from '../../../../contexts/AuthContext'
import { loadWorksheet, getCached, saveWorksheet } from '../../../../features/lesson-builder/lib/worksheetProgress'

// Câu hỏi TỰ LUẬN TRẢ LỜI NGẮN trong phiếu học tập:
// HS gõ câu trả lời (lưu server theo tài khoản) → có thể "Xem đáp án gợi ý" (GV/AI soạn) để tự đối chiếu,
// + "Askbot AI" gợi mở theo lối Socrates (không trả lời thay).
interface ShortAnswerQuestionProps {
  question: string
  sampleAnswer?: string      // đáp án gợi ý để HS tự đối chiếu
  hint?: string
  lessonId?: string
  itemKey: string            // khoá ổn định để lưu/khôi phục
}

const ShortAnswerQuestion: React.FC<ShortAnswerQuestionProps> = ({ question, sampleAnswer, hint, lessonId, itemKey }) => {
  const { user } = useAuth()
  const lid = lessonId || LessonStatsService.getCurrentLessonId() || undefined   // fallback: bài đang xem
  const [answer, setAnswer] = useState('')
  const [showHint, setShowHint] = useState(false)
  const [showSample, setShowSample] = useState(false)
  const [aiText, setAIText] = useState('')
  const [showAI, setShowAI] = useState(false)
  const [loadingAI, setLoadingAI] = useState(false)
  const dirtyRef = useRef(false)

  // Khôi phục câu trả lời đã lưu (server) khi mở lại.
  useEffect(() => {
    let alive = true
    loadWorksheet(lid).then(() => {
      if (!alive || dirtyRef.current) return
      const saved = getCached(lid, itemKey)?.answer
      if (saved) setAnswer(saved)
    })
    return () => { alive = false }
  }, [lid, itemKey])

  const onChange = (v: string) => {
    dirtyRef.current = true
    setAnswer(v)
    saveWorksheet(lid, itemKey, { answer: v })   // debounce
  }

  const handleAskAI = async () => {
    if (loadingAI) return
    if (!user) { setShowAI(true); setAIText('⚠️ Bạn cần đăng nhập để dùng Askbot AI.'); return }
    soundService.playAIThinking()
    LessonStatsService.recordAIQuestion(lid)
    setLoadingAI(true); setShowAI(true)
    try {
      const content = `${question}${answer.trim() ? `\n\nHS trả lời: ${answer.trim()}` : ''}`
      const res = await aiService.explainContent({
        content,
        contentType: 'socratic',
        context: 'Câu tự luận ngắn trong phiếu học tập Vật lí — HS đang tự làm, gợi mở để HS tự hoàn thiện câu trả lời',
        lessonId: lid,
        usage: { userId: user.id, feature: 'interactive_question', metadata: { lessonId: lid } },
      })
      setAIText(res.success && res.explanation ? res.explanation : (res.error || '⚠️ Không thể kết nối AI. Thử lại sau.'))
    } catch (e) {
      setAIText(e instanceof Error ? `⚠️ ${e.message}` : '⚠️ Có lỗi khi kết nối AI.')
    } finally {
      setLoadingAI(false)
    }
  }

  return (
    <div className="bg-gradient-to-br from-teal-50 to-cyan-50 rounded-lg border border-teal-200 p-2 sm:p-3 my-1 sm:my-2">
      <div className="flex items-center gap-1 mb-1">
        <span className="text-sm sm:text-base">✍️</span>
        <span className="px-1.5 py-0.5 rounded-full text-[0.6rem] sm:text-xs font-bold bg-teal-100 text-teal-800">Tự luận ngắn</span>
      </div>
      <div className="text-gray-800 leading-snug text-sm sm:text-base mb-2">
        <KaTeXRenderer content={question} />
      </div>

      <div className="mb-1 flex items-center gap-3">
        {hint && (
          <button onClick={() => { if (!showHint) LessonStatsService.recordHintUsed(lessonId); setShowHint(!showHint) }}
            className="flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-700">
            <Lightbulb className="w-3 h-3" /> {showHint ? 'Ẩn gợi ý' : 'Gợi ý'}
          </button>
        )}
        <button onClick={handleAskAI} disabled={loadingAI}
          className="flex items-center gap-1 text-xs font-medium text-purple-600 hover:text-purple-700 disabled:opacity-50">
          <Sparkles className="w-3 h-3" /> {loadingAI ? 'Askbot đang nghĩ…' : '🤖 Askbot AI'}
        </button>
      </div>

      {hint && showHint && (
        <div className="mb-2 p-1.5 bg-yellow-50 border border-yellow-200 rounded text-xs text-yellow-800">
          💡 <KaTeXRenderer content={hint} />
        </div>
      )}

      <textarea
        value={answer}
        onChange={(e) => onChange(e.target.value)}
        rows={3}
        placeholder="Nhập câu trả lời của em…"
        className="w-full p-2 border border-teal-200 rounded-lg text-sm leading-relaxed outline-none focus:ring-2 focus:ring-teal-200 resize-y bg-white"
      />

      {sampleAnswer && sampleAnswer.trim() && (
        <div className="mt-2">
          {!showSample ? (
            <button onClick={() => setShowSample(true)}
              className="inline-flex items-center gap-1.5 rounded-lg border border-teal-300 bg-white px-3 py-1.5 text-sm font-bold text-teal-700 hover:bg-teal-50">
              <Eye className="w-4 h-4" /> Xem đáp án gợi ý
            </button>
          ) : (
            <div className="p-2 bg-white rounded-lg border border-teal-200">
              <div className="flex items-center gap-1 text-xs font-bold text-teal-800 mb-1"><Check className="w-3.5 h-3.5" /> Đáp án gợi ý — em tự đối chiếu nhé</div>
              <div className="text-gray-700 text-sm leading-relaxed"><KaTeXRenderer content={sampleAnswer} /></div>
            </div>
          )}
        </div>
      )}

      {showAI && !loadingAI && (
        <div className="mt-2 p-2 rounded bg-gradient-to-br from-purple-50 to-pink-50 border border-purple-200">
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center gap-1 text-sm font-medium text-purple-800"><Sparkles className="w-4 h-4" /> Askbot AI — cùng suy nghĩ nhé</div>
            <button onClick={() => setShowAI(false)} className="text-xs font-medium text-purple-600 hover:text-purple-700">Ẩn</button>
          </div>
          <div className="p-1.5 bg-white rounded border border-purple-100 text-gray-700 text-sm leading-snug"><KaTeXRenderer content={aiText} /></div>
        </div>
      )}
    </div>
  )
}

export default ShortAnswerQuestion
